import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import {styled} from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import {format, startOfDay, endOfDay, subDays} from 'date-fns';
import React, {useState} from 'react';
import DatePicker from 'react-datepicker';
import {useSelector} from 'react-redux';

import {getDateFromText} from '../../utils/helper';
import CustomButton from '../CustomButton/CustomButton';
import CustomValueList from '../CustomList/CustomValueList';

import 'react-datepicker/dist/react-datepicker.css';

const StyledDatePickerBox = styled(Box)(({theme}) => ({
  margin: `${theme.spacing(1)} 0 ${theme.spacing(1)} 0`,
  '& .react-datepicker': {
    width: '100%',
  },
  '& .react-datepicker__month-container': {
    width: '100%',
  },
}));

const DateSelectorFilterMenu = ({
  addFilter,
  currentFilter,
  showMenuItemSelectBox,
  openCustomDateSelection,
  setOpenCustomDateSelection,
  showSelectedDisabled,
  virtualLoadWidth,
  virtualLoadHeight,
  allowDateRangeSelection,
}) => {
  let options = useSelector((store) => store.commonFilters[currentFilter?.key]);
  if (Array.isArray(options) && allowDateRangeSelection === false) {
    const customSelectionOption = options[options?.length - 1];
    options = options.slice(0, 2);
    options.push(customSelectionOption);
  }
  const [currentStartDate, currentEndDate] = currentFilter?.value || [];

  const [{startDate, endDate}, setDate] = useState({
    startDate: currentStartDate ? new Date(currentStartDate) : new Date(),
    endDate: currentEndDate ? new Date(currentEndDate) : new Date(),
  });

  const [{startDateText, endDateText}, setDateText] = useState({
    startDateText: format(
      startOfDay(currentStartDate ? new Date(currentStartDate) : new Date()),
      'dd MMM yyyy HH:mm:ss'
    ),
    endDateText: format(endOfDay(currentEndDate ? new Date(currentEndDate) : new Date()), 'dd MMM yyyy HH:mm:ss'),
  });

  const onChangeCalendar = (dates) => {
    let selectedDate = [];
    if (allowDateRangeSelection) {
      selectedDate = dates;
    } else {
      selectedDate = [dates, dates];
    }
    setDate({startDate: selectedDate[0], endDate: selectedDate[1]});
    setDateText({
      startDateText: format(startOfDay(new Date(selectedDate[0])), 'dd MMM yyyy HH:mm:ss'),
      endDateText: format(
        endOfDay(selectedDate[1] ? new Date(selectedDate[1]) : new Date(endOfDay(new Date(selectedDate[0])))),
        'dd MMM yyyy HH:mm:ss'
      ),
    });
  };

  const onClickMenu = (item) => {
    if (item?.label === 'Custom Date') {
      setOpenCustomDateSelection(true);
    } else {
      addFilter({
        ...currentFilter,
        isApplied: true,
        displayValue: item.label,
        value: getDateFromText(item.label),
      });
    }
  };

  const onCustomDateSelectionConfirm = () => {
    addFilter({
      ...currentFilter,
      isApplied: true,
      displayValue: `${format(new Date(startDate), 'dd MMM yyyy')} - ${format(
        endDate ? new Date(endDate) : new Date(startDate),
        'dd MMM yyyy'
      )}`,
      value: [new Date(startDateText).toISOString(), new Date(endDateText).toISOString()],
    });
  };

  const maxDate = new Date();
  const minDate = subDays(maxDate, 30);

  return openCustomDateSelection ? (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          onClick={() => {
            setOpenCustomDateSelection(false);
          }}
          variant="outlined"
          sx={{border: 'none', ': hover': {border: 'none'}}}
        >
          <NavigateBeforeIcon />
        </Button>
      </Box>
      <Box
        sx={{
          pr: 2,
          pl: 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <TextField
          label="Start Date"
          size="small"
          value={startDateText}
          sx={{
            marginTop: (theme) => theme.spacing(1.5),
          }}
          disabled={true}
        />
        <TextField
          label="End Date"
          size="small"
          value={endDateText}
          sx={{
            marginTop: (theme) => theme.spacing(1),
          }}
          disabled={true}
        />
        <StyledDatePickerBox>
          <DatePicker
            selected={startDate}
            onChange={onChangeCalendar}
            startDate={startDate}
            endDate={endDate}
            minDate={minDate}
            maxDate={maxDate}
            selectsRange={allowDateRangeSelection}
            inline
          />
        </StyledDatePickerBox>
        <CustomButton text={'Confirm'} onClick={onCustomDateSelectionConfirm} />
      </Box>
    </>
  ) : (
    <Box
      sx={{
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <CustomValueList
        items={options}
        onClick={onClickMenu}
        currentFilter={currentFilter}
        showMenuItemSelectBox={showMenuItemSelectBox}
        showSelectedDisabled={showSelectedDisabled}
        virtualLoadWidth={virtualLoadWidth}
        virtualLoadHeight={virtualLoadHeight}
      />
    </Box>
  );
};

export default DateSelectorFilterMenu;
